import { useMemo } from "react";

import type { PrayerSettings } from "@/data/types";
import {
  calculateTodaySchedule,
  calculateTomorrowSchedule,
} from "@/domain/prayer/calculatePrayerTimes";
import type { DaySchedule } from "@/domain/prayer/types";
import { formatIst } from "@/lib/time";

export interface ScheduleBundle {
  today: DaySchedule;
  tomorrow: DaySchedule;
}

/**
 * Memoised by IST calendar day. Recomputes only when settings change or the
 * IST date crosses midnight, so a 1s-ticking `now` does not thrash the engine.
 */
export function usePrayerSchedule(settings: PrayerSettings, now: Date): ScheduleBundle {
  const istDayKey = formatIst(now, "yyyy-MM-dd");
  return useMemo(
    () => ({
      today: calculateTodaySchedule(settings, now),
      tomorrow: calculateTomorrowSchedule(settings, now),
    }),
    // `now` intentionally omitted: only the IST day matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings, istDayKey],
  );
}
